import { useSystem } from 'api/systems/systems.api'
import { useHasPermission } from 'helpers/global.helper/global.helper'
import React, { ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import {
  periodExceedsSettlementOptimizationLimit,
  SETTLEMENT_OPTIMIZATION_MAX_PERIOD_MONTHS,
} from './SettlementOptimizeButton.helper'
import { SettlementOptimizeDialog } from './SettlementOptimizeDialog'
import { useSettlementOptimization } from './useSettlementOptimization'

type SettlementOptimizeButtonProps = {
  period: Period
}

export function SettlementOptimizeButton({ period }: SettlementOptimizeButtonProps): ReactNode {
  const { t } = useTranslation()
  const { systemId } = useAuth()
  const hasSettlementOptAccess = useHasPermission(`run_settlement_opt`)
  const { data: activeSystem } = useSystem({ id: systemId })
  const { runSettlementOptimization, isRunningSettlementOptimization } = useSettlementOptimization(period)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isPeriodTooLongDialogOpen, setIsPeriodTooLongDialogOpen] = useState(false)

  const settlementSubtypes = activeSystem?.subtypes?.settlement ?? []
  const triggerableSubtypes = settlementSubtypes.filter(
    (subtype: Subtype) => subtype.enable_manually_trigger_opt_job !== false
  )
  const periodExceedsLimit = periodExceedsSettlementOptimizationLimit(period)

  if (!hasSettlementOptAccess || settlementSubtypes.length === 0) {
    return null
  }

  return (
    <>
      <NewButton
        variant="optimize"
        label={t(`Optimize settlement`)}
        disabled={triggerableSubtypes.length === 0}
        onClick={() => (periodExceedsLimit ? setIsPeriodTooLongDialogOpen(true) : setIsDialogOpen(true))}
        loading={isRunningSettlementOptimization}
      />
      {isDialogOpen && (
        <SettlementOptimizeDialog
          period={period}
          subtypes={triggerableSubtypes}
          onClose={() => setIsDialogOpen(false)}
          onSubmit={runSettlementOptimization}
        />
      )}
      <ConfirmDialog
        open={isPeriodTooLongDialogOpen}
        onClose={() => setIsPeriodTooLongDialogOpen(false)}
        onConfirm={() => setIsPeriodTooLongDialogOpen(false)}
        textObject={{
          title: t(`Too long period`),
          text: ``,
          confirm: t(`Choose a new period`),
          deny: t('Cancel'),
        }}
        subTextList={[
          t(
            `Settlement optimization can only be run for a period of {{months}} month or less. Please choose a shorter period.`,
            { months: SETTLEMENT_OPTIMIZATION_MAX_PERIOD_MONTHS }
          ),
        ]}
      />
    </>
  )
}
